import { PDFDocument, rgb, PDFPage } from 'pdf-lib';
import { PhotoMarker } from '../types/types';
import { cropPdfRegionToBlob, renderPdfPageToBlob } from './cropPdfRegion';
import { downloadBlob } from './downloadBlob';

interface DownloadLayoutPDFOptions {
  fileName?: string;
  pageNumber?: number;
  rotation?: number;
  cropRegion?: [number, number, number, number] | null;
  flippedHorizontal?: boolean;
  flippedVertical?: boolean;
}

/**
 * Downloads a PDF layout with markers overlayed as vector graphics
 *
 * @param pdfUrl URL to the original PDF file
 * @param markers Array of photo markers to overlay
 * @param options Export options (file name, page number, crop region)
 */
export const downloadLayoutPDF = async (
  pdfUrl: string,
  markers: PhotoMarker[],
  options: DownloadLayoutPDFOptions = {}
): Promise<void> => {
  try {
    const {
      fileName = 'layout-with-markers',
      pageNumber = 1,
      rotation = 0,
      cropRegion = null,
      flippedHorizontal = false,
      flippedVertical = false,
    } = options;

    const normalizeRightAngle = (angle: number) => {
      const snapped = Math.round(angle / 90) * 90;
      return ((snapped % 360) + 360) % 360;
    };
    const normalizedRotation = normalizeRightAngle(rotation);
    // Render background using the same PDF.js raster path as the viewer.
    const renderedBackgroundBlob = cropRegion
      ? await cropPdfRegionToBlob(
          pdfUrl,
          pageNumber,
          {
            x: cropRegion[0],
            y: cropRegion[1],
            width: cropRegion[2],
            height: cropRegion[3],
          },
          72,
          null,
          normalizedRotation,
        )
      : await renderPdfPageToBlob(
          pdfUrl,
          pageNumber,
          72,
          null,
          normalizedRotation,
        );
    const backgroundBlob = flippedHorizontal || flippedVertical
      ? await flipImageBlob(renderedBackgroundBlob, flippedHorizontal, flippedVertical)
      : renderedBackgroundBlob;
    const backgroundBytes = new Uint8Array(await backgroundBlob.arrayBuffer());

    const outputPdf = await PDFDocument.create();
    const embeddedBackground = await outputPdf.embedPng(backgroundBytes);
    const outputWidth = cropRegion ? cropRegion[2] : embeddedBackground.width;
    const outputHeight = cropRegion ? cropRegion[3] : embeddedBackground.height;
    const outputPage = outputPdf.addPage([outputWidth, outputHeight]);
    outputPage.drawImage(embeddedBackground, {
      x: 0,
      y: 0,
      width: outputWidth,
      height: outputHeight,
    });

    // Normalize marker coordinates to exported crop-local space.
    // Some persisted marker sets are stored in full-layout coordinates.
    markers.forEach((marker) => {
      let markerX = marker.x;
      let markerY = marker.y;
      if (cropRegion) {
        const isOutsideCropBounds = (
          markerX < 0 ||
          markerY < 0 ||
          markerX > outputWidth ||
          markerY > outputHeight
        );
        if (isOutsideCropBounds) {
          markerX = markerX - cropRegion[0];
          markerY = markerY - cropRegion[1];
        }
      }
      drawMarkerOnPage(outputPage, marker, markerX, markerY);
    });

    // Serialize the modified PDF
    const modifiedPdfBytes = Uint8Array.from(await outputPdf.save());
    
    // Create blob and trigger download
    const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
    downloadBlob(blob, `${fileName}.pdf`);
  } catch (error) {
    console.error('Error downloading PDF with markers:', error);
    throw error;
  }
};

async function flipImageBlob(
  blob: Blob,
  flippedHorizontal: boolean,
  flippedVertical: boolean,
): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D canvas context');

  ctx.translate(flippedHorizontal ? bitmap.width : 0, flippedVertical ? bitmap.height : 0);
  ctx.scale(flippedHorizontal ? -1 : 1, flippedVertical ? -1 : 1);
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((flippedBlob) => {
      if (flippedBlob) resolve(flippedBlob);
      else reject(new Error('canvas.toBlob returned null'));
    }, 'image/png');
  });
}

/**
 * Draw a photo marker on a PDF page
 * Matches the red rectangle style from the viewer
 */
function drawMarkerOnPage(
  page: PDFPage,
  marker: PhotoMarker,
  markerX: number = marker.x,
  markerY: number = marker.y
): void {
  const { height: pageHeightPdf } = page.getSize();
  
  // Convert marker position from SVG coordinates to PDF coordinates
  // SVG origin is top-left, PDF origin is bottom-left
  const x = markerX;
  const y = pageHeightPdf - markerY; // Flip Y coordinate
  
  // Marker dimensions (matching the viewer)
  const markerWidth = marker.width || 50;
  const markerHeight = marker.height || 50;
  
  // Draw rectangle background (red fill, black stroke)
  // PDF coordinate system: origin at bottom-left
  const rectX = x - markerWidth / 2;
  const rectY = y - markerHeight / 2;
  
  page.drawRectangle({
    x: rectX,
    y: rectY,
    width: markerWidth,
    height: markerHeight,
    color: rgb(1, 0, 0), // Red fill
    borderColor: rgb(0, 0, 0), // Black stroke
    borderWidth: 1,
    opacity: 1,
  });
  
  // Draw marker number (white text, centered)
  const fontSize = marker.fontSize || 24;
  const numberText = marker.number.toString();
  
  // Estimate text width (rough approximation)
  const textWidth = numberText.length * (fontSize * 0.6);
  
  page.drawText(numberText, {
    x: x - textWidth / 2,
    y: y - fontSize / 3,
    size: fontSize,
    color: rgb(1, 1, 1), // White text
  });
}
